import React, { useState, useEffect } from 'react';
import { X, Search, Globe, Check, AlertTriangle, Loader2, Brain, Zap, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { subdomains } from '../services/api/client';

const SubdomainDiscoveryModal = ({
  isOpen,
  onClose,
  domain,
  onSubdomainsSelected,
  aiModel = 'sylink',
  scanType = 'quick'
}) => {
  const [discoveredSubdomains, setDiscoveredSubdomains] = useState([]);
  const [selectedSubdomains, setSelectedSubdomains] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [discoveryOptions, setDiscoveryOptions] = useState({
    use_subfinder: true,
    use_amass: false,
    use_dnsrecon: true,
    resolve_ips: true
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [attackMode, setAttackMode] = useState('ai_orchestrated'); // 'ai_orchestrated' | 'quick' | 'full'

  // Launch discovery when modal opens
  useEffect(() => {
    if (isOpen && domain) {
      discoverSubdomains();
    }
  }, [isOpen, domain]);

  const discoverSubdomains = async () => {
    setIsLoading(true);
    setError(null);
    setDiscoveredSubdomains([]);
    setSelectedSubdomains([]);

    try {
      const result = await subdomains.discover(domain, discoveryOptions);
      if (result.subdomains && result.subdomains.length > 0) {
        setDiscoveredSubdomains(result.subdomains);
        // Pre-select alive subdomains
        const aliveSubdomains = result.subdomains
          .filter(s => s.is_alive)
          .map(s => s.subdomain);
        setSelectedSubdomains(aliveSubdomains);
      } else {
        setDiscoveredSubdomains([]);
      }
    } catch (err) {
      console.error('Subdomain discovery error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to discover subdomains');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSubdomain = (subdomain) => {
    setSelectedSubdomains(prev => {
      if (prev.includes(subdomain)) {
        return prev.filter(s => s !== subdomain);
      } else {
        return [...prev, subdomain];
      }
    });
  };

  const selectAll = () => {
    setSelectedSubdomains(discoveredSubdomains.map(s => s.subdomain));
  };

  const selectAlive = () => {
    setSelectedSubdomains(discoveredSubdomains.filter(s => s.is_alive).map(s => s.subdomain));
  };

  const selectNone = () => {
    setSelectedSubdomains([]);
  };

  const handleAttack = () => {
    if (selectedSubdomains.length === 0) {
      setError('Please select at least one subdomain');
      return;
    }

    // Build subdomain data for selected ones
    const selectedData = discoveredSubdomains.filter(s =>
      selectedSubdomains.includes(s.subdomain)
    );

    onSubdomainsSelected({
      subdomains: selectedData,
      attackMode,
      aiModel
    });
    onClose();
  };

  if (!isOpen) return null;

  const aliveCount = discoveredSubdomains.filter(s => s.is_alive).length;
  const totalCount = discoveredSubdomains.length;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-brand-black-light border-2 border-brand-green/30 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-brand-green/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-brand-green/20">
                <Globe className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Subdomain Discovery</h2>
                <p className="text-gray-400 text-sm mt-1">
                  Discovering subdomains for <span className="text-brand-green font-mono">{domain}</span>
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-brand-black rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-400 hover:text-brand-green" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-12 h-12 text-brand-green animate-spin mb-4" />
              <p className="text-gray-400 text-center">
                Discovering subdomains...<br />
                <span className="text-xs text-gray-500">This may take a few minutes</span>
              </p>
              <div className="mt-4 flex gap-2 text-xs text-gray-500">
                {discoveryOptions.use_subfinder && <span className="px-2 py-1 bg-brand-green/10 rounded">Subfinder</span>}
                {discoveryOptions.use_amass && <span className="px-2 py-1 bg-brand-green/10 rounded">Amass</span>}
                {discoveryOptions.use_dnsrecon && <span className="px-2 py-1 bg-brand-green/10 rounded">DNSRecon</span>}
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-400 font-medium">Discovery Error</p>
                  <p className="text-gray-400 text-sm mt-1">{error}</p>
                </div>
                <button
                  onClick={discoverSubdomains}
                  className="px-3 py-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors text-sm flex items-center gap-1"
                >
                  <RefreshCw className="w-4 h-4" />
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Results */}
          {!isLoading && discoveredSubdomains.length > 0 && (
            <>
              {/* Summary */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <span className="text-gray-400">
                    Found <span className="text-brand-green font-bold">{totalCount}</span> subdomains
                    {aliveCount > 0 && (
                      <span className="ml-2">
                        (<span className="text-green-400">{aliveCount} alive</span>)
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={selectAll}
                    className="px-3 py-1 text-xs bg-brand-green/20 text-brand-green rounded hover:bg-brand-green/30 transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    onClick={selectAlive}
                    className="px-3 py-1 text-xs bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 transition-colors"
                  >
                    Select Alive
                  </button>
                  <button
                    onClick={selectNone}
                    className="px-3 py-1 text-xs bg-gray-700 text-gray-400 rounded hover:bg-gray-600 transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {/* Subdomain List */}
              <div className="border border-gray-800 rounded-lg overflow-hidden mb-6">
                <div className="max-h-[300px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-brand-black sticky top-0">
                      <tr className="text-xs text-gray-500">
                        <th className="p-3 text-left w-10">
                          <input
                            type="checkbox"
                            checked={selectedSubdomains.length === discoveredSubdomains.length}
                            onChange={(e) => e.target.checked ? selectAll() : selectNone()}
                            className="w-4 h-4 accent-brand-green"
                          />
                        </th>
                        <th className="p-3 text-left">Subdomain</th>
                        <th className="p-3 text-left">IP Address</th>
                        <th className="p-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {discoveredSubdomains.map((sub, idx) => (
                        <tr
                          key={idx}
                          onClick={() => toggleSubdomain(sub.subdomain)}
                          className={`border-t border-gray-800 cursor-pointer transition-colors ${
                            selectedSubdomains.includes(sub.subdomain)
                              ? 'bg-brand-green/10'
                              : 'hover:bg-brand-black'
                          }`}
                        >
                          <td className="p-3">
                            <input
                              type="checkbox"
                              checked={selectedSubdomains.includes(sub.subdomain)}
                              onChange={() => toggleSubdomain(sub.subdomain)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 accent-brand-green"
                            />
                          </td>
                          <td className="p-3 font-mono text-sm text-white">{sub.subdomain}</td>
                          <td className="p-3 font-mono text-sm text-gray-400">
                            {sub.ip_addresses?.join(', ') || '-'}
                          </td>
                          <td className="p-3 text-center">
                            {sub.is_alive ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">
                                <Check className="w-3 h-3" /> Alive
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 bg-gray-700 text-gray-500 rounded text-xs">
                                Unknown
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Attack Mode Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-400 mb-3">Attack Mode</label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setAttackMode('ai_orchestrated')}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      attackMode === 'ai_orchestrated'
                        ? 'border-brand-green bg-brand-green/10'
                        : 'border-gray-800 hover:border-brand-green/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Brain className="w-5 h-5 text-brand-green" />
                      <span className="font-semibold text-white">AI Orchestrated</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      L'IA LLM orchestre les attaques intelligemment
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setAttackMode('quick')}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      attackMode === 'quick'
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-gray-800 hover:border-blue-500/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-5 h-5 text-blue-400" />
                      <span className="font-semibold text-white">Scan rapide</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Scan rapide des ports et services
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setAttackMode('full')}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      attackMode === 'full'
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-gray-800 hover:border-purple-500/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Search className="w-5 h-5 text-purple-400" />
                      <span className="font-semibold text-white">Scan complet</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Full analysis with vulnerabilities
                    </p>
                  </button>
                </div>
              </div>

              {/* Advanced Options Toggle */}
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm text-gray-400 hover:text-brand-green transition-colors mb-3"
              >
                {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                Advanced Discovery Options
              </button>

              {showAdvanced && (
                <div className="bg-brand-black rounded-lg p-4 border border-gray-800 mb-4">
                  <div className="grid grid-cols-2 gap-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={discoveryOptions.use_subfinder}
                        onChange={(e) => setDiscoveryOptions({...discoveryOptions, use_subfinder: e.target.checked})}
                        className="w-4 h-4 accent-brand-green"
                      />
                      <div>
                        <div className="text-sm text-white">Subfinder</div>
                        <div className="text-xs text-gray-500">Fast passive enumeration</div>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={discoveryOptions.use_amass}
                        onChange={(e) => setDiscoveryOptions({...discoveryOptions, use_amass: e.target.checked})}
                        className="w-4 h-4 accent-brand-green"
                      />
                      <div>
                        <div className="text-sm text-white">Amass</div>
                        <div className="text-xs text-gray-500">Deep enumeration (slower)</div>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={discoveryOptions.use_dnsrecon}
                        onChange={(e) => setDiscoveryOptions({...discoveryOptions, use_dnsrecon: e.target.checked})}
                        className="w-4 h-4 accent-brand-green"
                      />
                      <div>
                        <div className="text-sm text-white">DNSRecon</div>
                        <div className="text-xs text-gray-500">DNS zone analysis</div>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={discoveryOptions.resolve_ips}
                        onChange={(e) => setDiscoveryOptions({...discoveryOptions, resolve_ips: e.target.checked})}
                        className="w-4 h-4 accent-brand-green"
                      />
                      <div>
                        <div className="text-sm text-white">Resolve IPs</div>
                        <div className="text-xs text-gray-500">Get IP addresses for each subdomain</div>
                      </div>
                    </label>
                  </div>

                  <button
                    onClick={discoverSubdomains}
                    className="mt-4 px-4 py-2 bg-brand-green/20 text-brand-green rounded hover:bg-brand-green/30 transition-colors text-sm flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Re-run Discovery
                  </button>
                </div>
              )}
            </>
          )}

          {/* No Results */}
          {!isLoading && !error && discoveredSubdomains.length === 0 && (
            <div className="text-center py-16">
              <Globe className="w-16 h-16 text-gray-700 mx-auto mb-4" />
              <p className="text-gray-400">No subdomains discovered</p>
              <p className="text-xs text-gray-500 mt-2">
                Try enabling more discovery tools or check the domain
              </p>
              <button
                onClick={discoverSubdomains}
                className="mt-4 px-4 py-2 bg-brand-green/20 text-brand-green rounded hover:bg-brand-green/30 transition-colors text-sm flex items-center gap-2 mx-auto"
              >
                <RefreshCw className="w-4 h-4" />
                Retry Discovery
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-brand-green/20">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-400">
              {selectedSubdomains.length > 0 && (
                <span>
                  <span className="text-brand-green font-bold">{selectedSubdomains.length}</span> subdomains selected
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-3 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAttack}
                disabled={selectedSubdomains.length === 0 || isLoading}
                className="btn-green disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {attackMode === 'ai_orchestrated' && <Brain className="w-4 h-4" />}
                {attackMode === 'quick' && <Zap className="w-4 h-4" />}
                {attackMode === 'full' && <Search className="w-4 h-4" />}
                Attack Selected ({selectedSubdomains.length})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubdomainDiscoveryModal;
